/**
 * Banco de efectos de sonido.
 *
 * Reproduce las piezas reales del catálogo y, cuando una no existe o no se
 * pudo cargar, delega en el generador procedural para que la interfaz nunca se
 * quede muda.
 */

import { ProceduralAudio } from '../render/procedural-audio';
import { AUDIO_BASE_URL, SFX_LIBRARY, resolveSfxName, type SfxName } from './library';

const SFX_VOLUME = 0.6;

export class SfxBank {
  private readonly elements = new Map<SfxName, HTMLAudioElement>();
  private readonly broken = new Set<SfxName>();
  private enabled = true;
  private reducedMotion = false;

  constructor(
    private readonly procedural: ProceduralAudio,
    private readonly baseUrl: string = AUDIO_BASE_URL
  ) {}

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  setReducedMotion(value: boolean): void {
    this.reducedMotion = value;
  }

  /** Reproduce un efecto por nombre; acepta el vocabulario antiguo del JSON. */
  play(name: string, volume: number = SFX_VOLUME): void {
    if (!this.enabled || this.reducedMotion) return;

    const resolved = resolveSfxName(name);
    if (!resolved || this.broken.has(resolved)) {
      this.procedural.playSfxByName(name);
      return;
    }

    const el = this.ensure(resolved);
    if (!el) {
      this.procedural.playSfxByName(name);
      return;
    }
    el.volume = volume;
    try {
      el.currentTime = 0;
    } catch {
      /* aún sin datos decodificables; play() los pedirá */
    }
    void el.play().catch(() => undefined);
  }

  /** Precarga un efecto para que suene sin retardo la primera vez. */
  preload(name: SfxName): void {
    this.ensure(name);
  }

  private ensure(name: SfxName): HTMLAudioElement | null {
    const cached = this.elements.get(name);
    if (cached) return cached;
    try {
      const el = new Audio();
      el.preload = 'auto';
      el.addEventListener('error', () => {
        this.broken.add(name);
        this.elements.delete(name);
      });
      el.src = this.baseUrl + SFX_LIBRARY[name].src;
      this.elements.set(name, el);
      return el;
    } catch {
      return null;
    }
  }

  destroy(): void {
    for (const el of this.elements.values()) {
      el.pause();
      el.removeAttribute('src');
    }
    this.elements.clear();
  }
}
